import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Settings as GeneralIcon,
  Extension as FlowableIcon,
  Widgets as AmtIcon,
} from '@mui/icons-material';
import { GeneralPropertiesEditor } from './GeneralPropertiesEditor';
import { FlowablePropertiesEditor } from './FlowablePropertiesEditor';
import { TaskDisplayEditor } from './TaskDisplayEditor';
import { FormDefinitionEditor } from './FormDefinitionEditor';
import { ProcessConfigEditor } from './ProcessConfigEditor';
import type { BpmnElement } from '../../hooks/useBpmnModeler';
import type {
  AmtTaskDisplay,
  AmtFormDefinition,
  AmtProcessConfig,
  AmtFormSection,
  AmtFormField,
} from '../../utils/amtModdleDescriptor';

// --- Moddle → TypeScript normalization ---
// Moddle stores multilingual values as arrays of {lang, value} objects,
// while the TypeScript interfaces expect Record<string, string>.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function moddleLangsToRecord(langs: any[] | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!Array.isArray(langs)) return result;
  for (const item of langs) {
    if (item?.lang && item?.value !== undefined) {
      result[item.lang] = String(item.value);
    }
  }
  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeTaskDisplay(raw: any): AmtTaskDisplay {
  return {
    labels: moddleLangsToRecord(raw.labels),
    descriptions: moddleLangsToRecord(raw.descriptions),
    priority: raw.priority || 'MEDIUM',
    icon: raw.icon,
    color: raw.color,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeFormDefinition(raw: any): AmtFormDefinition {
  return {
    id: raw.id || '',
    sections: (raw.sections || []).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (section: any): AmtFormSection => ({
        id: section.id || '',
        titles: moddleLangsToRecord(section.titles),
        descriptions: moddleLangsToRecord(section.descriptions),
        collapsible: section.collapsible === true || section.collapsible === 'true',
        collapsed: section.collapsed === true || section.collapsed === 'true',
        fields: (section.fields || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (field: any): AmtFormField => ({
            id: field.id || '',
            type: field.type || 'TEXT',
            processVariable: field.processVariable || '',
            labels: moddleLangsToRecord(field.labels),
            placeholders: moddleLangsToRecord(field.placeholders),
            hints: moddleLangsToRecord(field.hints),
            required: field.required === true || field.required === 'true',
            readOnly: field.readOnly === true || field.readOnly === 'true',
            defaultValue: field.defaultValue,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            options: (field.options?.option || []).map((opt: any) => ({
              value: opt.value || '',
              labels: moddleLangsToRecord(opt.labels),
            })),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            validationRules: (field.validation?.rules || []).map((rule: any) => ({
              type: rule.type,
              value: rule.value,
              messages: moddleLangsToRecord(rule.messages),
            })),
            conditional: field.conditional
              ? {
                  field: field.conditional.field || '',
                  operator: field.conditional.operator || 'EQUALS',
                  value: field.conditional.value || '',
                }
              : undefined,
          })
        ),
      })
    ),
  };
}

interface IntegratedPropertiesPanelProps {
  element: BpmnElement | null;
  onUpdateProperty: (property: string, value: unknown) => void;
  onUpdateName: (name: string) => void;
  onUpdateDocumentation: (text: string) => void;
  onUpdateAmtExtension: (amtType: 'TaskDisplay' | 'FormDefinition' | 'ProcessConfig', data: AmtTaskDisplay | AmtFormDefinition | AmtProcessConfig, targetElement?: BpmnElement) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ pt: 2, height: '100%', overflow: 'auto' }}
    >
      {value === index && children}
    </Box>
  );
}

export function IntegratedPropertiesPanel({
  element,
  onUpdateProperty,
  onUpdateName,
  onUpdateDocumentation,
  onUpdateAmtExtension,
}: IntegratedPropertiesPanelProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [taskDisplay, setTaskDisplay] = useState<AmtTaskDisplay | null>(null);
  const [formDefinition, setFormDefinition] = useState<AmtFormDefinition | null>(null);
  const [processConfig, setProcessConfig] = useState<AmtProcessConfig | null>(null);

  const isUserTask = element?.type === 'bpmn:UserTask';
  const isProcess = element?.type === 'bpmn:Process' || element?.type === 'bpmn:Participant';

  // Parse AMT extensions when a *different* element is selected.
  // Depends on element ID only — not the object reference — so that editing
  // Flowable properties (which mutate the same element in-place) does not
  // trigger a re-parse + state cascade that steals focus from input fields.
  useEffect(() => {
    if (!element) {
      setTaskDisplay(null);
      setFormDefinition(null);
      setProcessConfig(null);
      return;
    }

    const extensions = element.businessObject?.extensionElements?.values || [];

    const existingTaskDisplay = extensions.find((ext) => ext.$type === 'amt:TaskDisplay');
    const existingFormDef = extensions.find((ext) => ext.$type === 'amt:FormDefinition');

    if (existingTaskDisplay) {
      setTaskDisplay(normalizeTaskDisplay(existingTaskDisplay));
    } else {
      setTaskDisplay(null);
    }

    if (existingFormDef) {
      setFormDefinition(normalizeFormDefinition(existingFormDef));
    } else {
      setFormDefinition(null);
    }

    // Parse ProcessConfig from process-level extensions
    if (isProcess) {
      const processExt = extensions.find((ext) => ext.$type === 'amt:ProcessConfig');
      if (processExt) {
        setProcessConfig({
          returnChannelEnabled: processExt.returnChannelEnabled !== false,
        });
      } else {
        setProcessConfig(null);
      }
    } else {
      setProcessConfig(null);
    }
  }, [element?.id, isProcess]);

  const handleTaskDisplayChange = useCallback((value: AmtTaskDisplay) => {
    setTaskDisplay(value);
    onUpdateAmtExtension('TaskDisplay', value);
  }, [onUpdateAmtExtension]);

  const handleFormDefinitionChange = useCallback((value: AmtFormDefinition) => {
    setFormDefinition(value);
    onUpdateAmtExtension('FormDefinition', value);
  }, [onUpdateAmtExtension]);

  const handleProcessConfigChange = useCallback((value: AmtProcessConfig) => {
    setProcessConfig(value);
    onUpdateAmtExtension('ProcessConfig', value);
  }, [onUpdateAmtExtension]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!element) {
    return (
      <Paper
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Eigenschaften
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          Waehlen Sie ein Element im Diagramm aus, um seine Eigenschaften zu bearbeiten.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h6" noWrap>
          Eigenschaften
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {element.businessObject?.name || element.id}
        </Typography>
      </Box>

      <Divider />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 42 }}
      >
        <Tab
          icon={<GeneralIcon fontSize="small" />}
          label="Allgemein"
          iconPosition="start"
          sx={{ minHeight: 42, py: 0 }}
        />
        <Tab
          icon={<FlowableIcon fontSize="small" />}
          label="Flowable"
          iconPosition="start"
          sx={{ minHeight: 42, py: 0 }}
        />
        {(isUserTask || isProcess) && (
          <Tab
            icon={<AmtIcon fontSize="small" />}
            label="AMT"
            iconPosition="start"
            sx={{ minHeight: 42, py: 0 }}
          />
        )}
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <TabPanel value={activeTab} index={0}>
          <GeneralPropertiesEditor
            element={element}
            onUpdateName={onUpdateName}
            onUpdateDocumentation={onUpdateDocumentation}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <FlowablePropertiesEditor
            element={element}
            onUpdateProperty={onUpdateProperty}
          />
        </TabPanel>

        {(isUserTask || isProcess) && (
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="subtitle2" color="primary">
                AMT-Erweiterungen
              </Typography>

              {isProcess && (
                <ProcessConfigEditor
                  value={processConfig}
                  onChange={handleProcessConfigChange}
                />
              )}

              {isUserTask && (
                <>
                  <TaskDisplayEditor
                    value={taskDisplay}
                    onChange={handleTaskDisplayChange}
                  />

                  <Divider />

                  <FormDefinitionEditor
                    value={formDefinition}
                    onChange={handleFormDefinitionChange}
                  />
                </>
              )}
            </Box>
          </TabPanel>
        )}
      </Box>
    </Paper>
  );
}
